import { fromDate, getLocalTimeZone, isToday } from '@internationalized/date';
import { clsx } from 'clsx';
import React, { PropsWithChildren, RefObject } from 'react';
import {
  Calendar as AriaCalendar,
  Button,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateValue,
  Dialog,
  Heading,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import IconButton from '~/components/buttons/NewIconButton';
import Popover from '~/components/popovers/Popover/Popover';
import Text from '~/components/typographies/Text';
import LeftIcon from '~/icons/icon-chevron-left.svg?react';
import RightIcon from '~/icons/icon-chevron-right.svg?react';
import IconFlash from '~/icons/icon-flash.svg?react';
import { formatDateForDisplay } from '~/utils/formatDate';
import styles from './DatePicker.module.scss';

interface PresetDate {
  date: Date;
  id: string;
  label: string;
}

interface Props {
  defaultValue?: Date | null | undefined;
  isDateUnavailable?: (date: DateValue) => boolean;
  isOpen?: boolean | undefined;
  onOpenChange?: (isOpen: boolean) => void | undefined;
  onSelectDate?: (date: DateValue) => void | undefined;
  onSelectPreset?: (date: Date) => void | undefined;
  presetDates?: PresetDate[];
  triggerRef?: RefObject<HTMLElement | null> | undefined;
  isNonModal?: boolean | undefined;
}

const MaybeDialog: React.FC<PropsWithChildren<{ isNonModal?: boolean; ariaLabel?: string }>> = ({
  isNonModal,
  ariaLabel,
  children,
}) => {
  return isNonModal ? (
    <div className={styles.dialog}>{children}</div>
  ) : (
    <Dialog aria-label={ariaLabel} className={styles.dialog}>
      {children}
    </Dialog>
  );
};

const CalendarPopover = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      defaultValue,
      isDateUnavailable,
      isOpen,
      onOpenChange,
      onSelectDate,
      onSelectPreset,
      presetDates,
      triggerRef,
      isNonModal,
    },
    ref,
  ) => {
    const { t } = useTranslation('inputs');
    const localTimeZone = getLocalTimeZone();
    const calendarLabel = t('date_picker.calendar');

    return (
      <Popover
        className={styles.popover}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        triggerRef={triggerRef}
        isNonModal={isNonModal}
        ref={ref}
      >
        <MaybeDialog isNonModal={isNonModal} ariaLabel={calendarLabel}>
          {presetDates && (
            <div className={styles.presetDates}>
              <Text variant="body2" weight="semibold" className={styles.presetTitle}>
                {t('date_picker.quick_selections')}
                <IconFlash className={styles.iconFlash} />
              </Text>
              <div className={styles.presetList}>
                {presetDates.map((presetDate) => (
                  <Button
                    className={styles.preset}
                    key={presetDate.id}
                    onPress={() => {
                      onSelectPreset?.(presetDate.date);
                    }}
                  >
                    <span className={styles.label}>{presetDate.label}</span>
                    <span className={styles.detail}>{formatDateForDisplay(presetDate.date)}</span>
                  </Button>
                ))}
              </div>
            </div>
          )}
          <AriaCalendar
            className={styles.calendar}
            isDateUnavailable={isDateUnavailable}
            onChange={onSelectDate}
            defaultValue={defaultValue ? fromDate(defaultValue, localTimeZone) : undefined}
          >
            <div className={styles.header}>
              <IconButton Icon={LeftIcon} slot="previous" variant="ghost" />
              <Heading className={styles.heading} />
              <IconButton Icon={RightIcon} slot="next" variant="ghost" />
            </div>
            <CalendarGrid className={styles.grid} weekdayStyle="short">
              <CalendarGridHeader>
                {(day) => <CalendarHeaderCell className={styles.headerCell}>{day}</CalendarHeaderCell>}
              </CalendarGridHeader>
              <CalendarGridBody>
                {(date) => (
                  <CalendarCell
                    className={({ isDisabled, isUnavailable }) =>
                      clsx(styles.calendarCell, {
                        [styles.disabled!]: isDisabled || isUnavailable,
                        [styles.today!]: isToday(date, localTimeZone),
                      })
                    }
                    date={date}
                  />
                )}
              </CalendarGridBody>
            </CalendarGrid>
          </AriaCalendar>
        </MaybeDialog>
      </Popover>
    );
  },
);

export default CalendarPopover;
